// =============================================================================
// components/ai/ghostSuggestions.ts
// R2 Ghost Command Suggestions — context-aware autocomplete for R2 Chat Panel
// =============================================================================

export interface GhostSuggestion {
  id: string;
  text: string;                     // Full suggestion text
  trigger?: string;                 // Partial input to match (prefix)
  routes?: string[];                // Active when on these routes
  objectTypes?: string[];           // Active when this discipline is selected
  keywords?: string[];              // Activated when input contains these words
  priority: number;                 // Higher = shown first
}

// ─── Suggestion Bank ──────────────────────────────────────────────────────────

export const GHOST_SUGGESTIONS: GhostSuggestion[] = [
  // ── SmartViewer (general) ─────────────────────────────────────────────────
  { id: 'sv-1', text: 'Show me all telecom rooms in this building', routes: ['r2-smart-viewer'], priority: 10 },
  { id: 'sv-2', text: 'Highlight all ICT equipment on Level 3', routes: ['r2-smart-viewer'], priority: 9 },
  { id: 'sv-3', text: 'What clashes exist in the MDF room?', routes: ['r2-smart-viewer'], priority: 8 },
  { id: 'sv-4', text: 'Show all MDF and IDF room locations', routes: ['r2-smart-viewer'], priority: 7 },
  { id: 'sv-5', text: 'List all equipment missing commissioning data', routes: ['r2-smart-viewer'], priority: 6 },
  { id: 'sv-6', text: 'Generate a digital twin status report', routes: ['r2-smart-viewer'], priority: 5 },

  // ── SmartViewer — ICT object selected ────────────────────────────────────
  { id: 'sv-ict-1', text: 'What documents are missing for this equipment?', routes: ['r2-smart-viewer'], objectTypes: ['ict'], priority: 15 },
  { id: 'sv-ict-2', text: 'Show installation history for this rack', routes: ['r2-smart-viewer'], objectTypes: ['ict'], priority: 14 },
  { id: 'sv-ict-3', text: 'Create a commissioning checklist for this item', routes: ['r2-smart-viewer'], objectTypes: ['ict'], priority: 13 },
  { id: 'sv-ict-4', text: 'What are the pending issues for this object?', routes: ['r2-smart-viewer'], objectTypes: ['ict'], priority: 12 },
  { id: 'sv-ict-5', text: 'Generate an O&M task for this equipment', routes: ['r2-smart-viewer'], objectTypes: ['ict'], priority: 11 },

  // ── SmartViewer — Structural/MEP selected ────────────────────────────────
  { id: 'sv-mep-1', text: 'Check clearances for this MEP element', routes: ['r2-smart-viewer'], objectTypes: ['mep', 'structural', 'hvac'], priority: 12 },
  { id: 'sv-mep-2', text: 'Are there any clashes with this element?', routes: ['r2-smart-viewer'], objectTypes: ['mep', 'structural'], priority: 11 },

  // ── Commissioning / CTDTE ────────────────────────────────────────────────
  { id: 'ctdte-1', text: 'Show all open commissioning tasks', routes: ['r2-ctdte'], priority: 10 },
  { id: 'ctdte-2', text: 'What items are overdue for inspection?', routes: ['r2-ctdte'], priority: 9 },
  { id: 'ctdte-3', text: 'Which systems are ready for final sign-off?', routes: ['r2-ctdte'], priority: 8 },
  { id: 'ctdte-4', text: 'Summarize commissioning progress this week', routes: ['r2-ctdte'], priority: 7 },
  { id: 'ctdte-5', text: 'Generate a handover package checklist', routes: ['r2-ctdte'], priority: 6 },
  { id: 'ctdte-6', text: 'What documents are missing for turnover?', routes: ['r2-ctdte'], priority: 5 },

  // ── Meeting Intelligence (MIE) ────────────────────────────────────────────
  { id: 'mie-1', text: 'Summarize the last coordination meeting', routes: ['r2-meeting-intelligence'], priority: 10 },
  { id: 'mie-2', text: 'What action items are overdue from last meeting?', routes: ['r2-meeting-intelligence'], priority: 9 },
  { id: 'mie-3', text: 'Prepare an agenda for the next site meeting', routes: ['r2-meeting-intelligence'], priority: 8 },
  { id: 'mie-4', text: 'Who has the most open action items?', routes: ['r2-meeting-intelligence'], priority: 7 },
  { id: 'mie-5', text: 'Extract all RFIs raised in recent meetings', routes: ['r2-meeting-intelligence'], priority: 6 },

  // ── Permit Intelligence (PIE) ─────────────────────────────────────────────
  { id: 'pie-1', text: 'What permits are required for this fibre route?', routes: ['r2-permit-intelligence'], priority: 10 },
  { id: 'pie-2', text: 'Check if this project has all required permit documents', routes: ['r2-permit-intelligence'], priority: 9 },
  { id: 'pie-3', text: 'Summarize outstanding permit issues', routes: ['r2-permit-intelligence'], priority: 8 },
  { id: 'pie-4', text: 'Generate a road crossing permit checklist', routes: ['r2-permit-intelligence'], priority: 7 },
  { id: 'pie-5', text: 'What permits are at risk of expiring this month?', routes: ['r2-permit-intelligence'], priority: 6 },
  { id: 'pie-6', text: 'Prepare a revision response for the municipality comments', routes: ['r2-permit-intelligence'], priority: 5 },

  // ── Mission Control ───────────────────────────────────────────────────────
  { id: 'mc-1', text: 'What is the current project health score?', routes: ['r2-mission-control'], priority: 10 },
  { id: 'mc-2', text: 'Show AI usage summary this week', routes: ['r2-mission-control'], priority: 9 },
  { id: 'mc-3', text: 'Which systems need immediate attention?', routes: ['r2-mission-control'], priority: 8 },
  { id: 'mc-4', text: 'Generate an executive project status summary', routes: ['r2-mission-control'], priority: 7 },

  // ── Dashboard / General ───────────────────────────────────────────────────
  { id: 'gen-1', text: 'What is the overall project status?', routes: ['dashboard'], priority: 10 },
  { id: 'gen-2', text: 'Show me this week\'s critical items', routes: ['dashboard'], priority: 9 },
  { id: 'gen-3', text: 'What should I prioritize today?', routes: ['dashboard'], priority: 8 },
  { id: 'gen-4', text: 'Summarize all open RFIs and their status', routes: ['dashboard'], priority: 7 },

  // ── Annotations ───────────────────────────────────────────────────────────
  { id: 'ann-1', text: 'Show all critical annotations on this project', routes: ['r2-annotations'], priority: 10 },
  { id: 'ann-2', text: 'Which punch list items are still open?', routes: ['r2-annotations'], priority: 9 },
  { id: 'ann-3', text: 'Generate a QA/QC report for this section', routes: ['r2-annotations'], priority: 8 },

  // ── R2 Core Map (Founder Mode) ────────────────────────────────────────────
  { id: 'core-1', text: 'Explain the R2 Intelligence Engine architecture', routes: ['r2-core-map'], priority: 10 },
  { id: 'core-2', text: 'What is the roadmap for R2 Digital Twin World?', routes: ['r2-core-map'], priority: 9 },
  { id: 'core-3', text: 'Compare R2 engines by capability and status', routes: ['r2-core-map'], priority: 8 },

  // ── Global (any route) — trigger-based ───────────────────────────────────
  { id: 'gl-show', text: 'show me all open issues for this project', trigger: 'show', priority: 5 },
  { id: 'gl-what', text: 'what are the critical risks this week?', trigger: 'what', priority: 4 },
  { id: 'gl-gen',  text: 'generate a project status report',         trigger: 'gen', priority: 4 },
  { id: 'gl-sum',  text: 'summarize the current project status',     trigger: 'sum', priority: 4 },
  { id: 'gl-r2',   text: 'R2, analyze the current module and identify key issues', trigger: 'r2,', priority: 6 },
  { id: 'gl-create', text: 'create a task for this issue',           trigger: 'create', priority: 4 },
  { id: 'gl-find',   text: 'find all unresolved RFIs',               trigger: 'find', priority: 4 },
  { id: 'gl-check',  text: 'check compliance requirements for this project', trigger: 'check', priority: 4 },
  { id: 'gl-list',   text: 'list all pending action items assigned to me', trigger: 'list', priority: 4 },
  { id: 'gl-how',    text: 'how many permits are currently under review?', trigger: 'how', priority: 4 },
  { id: 'gl-who',    text: 'who is responsible for this issue?',     trigger: 'who', priority: 4 },
];

// ─── Suggestion Resolver ──────────────────────────────────────────────────────

export function resolveGhostSuggestion(
  input: string,
  currentRoute: string,
  selectedObjectDiscipline?: string | null,
): GhostSuggestion | null {
  const q = input.toLowerCase().trim();
  const route = currentRoute?.toLowerCase() ?? '';

  // Don't show if input is long enough to be complete
  if (q.length > 60) return null;

  let candidates: GhostSuggestion[] = [];

  for (const s of GHOST_SUGGESTIONS) {
    // Route filter
    const routeMatch = !s.routes || s.routes.some(r => route.includes(r));
    if (!routeMatch) continue;

    // Object type filter
    if (s.objectTypes && selectedObjectDiscipline) {
      const objMatch = s.objectTypes.some(t =>
        selectedObjectDiscipline.toLowerCase().includes(t)
      );
      if (!objMatch && s.objectTypes.length > 0) continue;
    } else if (s.objectTypes && !selectedObjectDiscipline) {
      continue; // Requires object, none selected
    }

    // Trigger match (starts-with prefix on input)
    if (s.trigger) {
      if (!q.startsWith(s.trigger.toLowerCase())) continue;
      // Suggestion must be longer than what's typed
      if (s.text.toLowerCase() === q) continue;
    }

    candidates.push(s);
  }

  if (candidates.length === 0) return null;

  // Sort by priority descending, then pick first that isn't already typed
  candidates.sort((a, b) => b.priority - a.priority);

  // If user has typed something, find suggestions where text starts with what they typed
  if (q.length >= 2) {
    const prefixMatch = candidates.find(s =>
      s.text.toLowerCase().startsWith(q) && s.text.toLowerCase() !== q
    );
    if (prefixMatch) return prefixMatch;

    // Also check for trigger match where suggestion is meaningfully longer
    const triggerMatch = candidates.find(s =>
      s.trigger && q.startsWith(s.trigger.toLowerCase()) && s.text.toLowerCase() !== q
    );
    if (triggerMatch) return triggerMatch;

    return null; // No relevant prefix match — don't show generic suggestions when typing
  }

  // Empty input or very short — show top priority route-specific suggestion
  if (q.length === 0) {
    // Only show if route-specific
    const routeSpecific = candidates.find(s => s.routes && s.routes.length > 0 && !s.trigger);
    return routeSpecific ?? null;
  }

  return null;
}
